import React, { useContext, useState } from 'react'
import {
  Box,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { Product } from 'hooks/useProduct'
import { CartContext } from 'contexts/CartContext'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { PRODUCT_IMAGE_BASE_URL } from 'config'
import { AuthContext } from 'contexts/AuthContext'

type ProductItemProps = {
  cabinetTypeKey: string
  itemData: Product[]
}

const ProductItem: React.FC<ProductItemProps> = ({
  cabinetTypeKey,
  itemData
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { updateCartItem } = useContext(CartContext)
  const { showMessage } = useContext(SnackbarContext)
  const [hinges, setHinges] = useState<{ [key: string]: string }>({})
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({})
  const [isAssembled, setIsAssembled] = useState<{ [key: string]: boolean }>({})
  const { isAuthenticated } = useContext(AuthContext)

  const handleQuantityChange = (sku: string, quantity: number) => {
    setQuantities(prev => ({
      ...prev,
      [sku]: quantity
    }))
  }

  const handleHingeChange = (sku: string, hinge: string) => {
    setHinges(prev => ({
      ...prev,
      [sku]: hinge
    }))
  }

  const handleAddToCart = (item: Product) => {
    const quantity = quantities[item.sku] || 1
    const assembled = isAssembled[item.sku] || false
    let hinge: string | undefined = ''

    if (item.isHingeable && assembled) {
      hinge = hinges[item.sku] || 'LEFT'
    }

    try {
      updateCartItem({
        productId: item.id,
        quantity,
        isAssembled: assembled,
        hingeDirection: hinge
      })
    } catch (error) {
      console.error('Failed to update cart item:', error)
      showMessage(t('snackbar.cart.addFailed'), 'error')
    }
  }

  const handleAssembledChange = (sku: string) => {
    setIsAssembled(prev => {
      const newIsAssembled = {
        ...prev,
        [sku]: !prev[sku]
      }

      if (!newIsAssembled[sku]) {
        setHinges(prevHinges => ({
          ...prevHinges,
          [sku]: ''
        }))
      } else {
        setHinges(prevHinges => ({
          ...prevHinges,
          [sku]: prevHinges[sku] || 'LEFT'
        }))
      }

      return newIsAssembled
    })
  }

  return (
    <Box sx={{ marginBottom: '40px' }}>
      <Typography
        variant='h3'
        sx={{
          marginBottom: '16px'
        }}
      >
        {cabinetTypeKey}
      </Typography>

      {cabinetTypeKey === 'Base Easy Reach' && (
        <Typography
          variant='body2'
          sx={{
            fontStyle: 'italic',
            color: 'secondary.main',
            marginBottom: '16px'
          }}
        >
          *If you would like to make this cabinet a Lazy Susan, please add a
          Wooden Table to your cart below.
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              width: '190px',
              height: '190px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Box
              component='img'
              src={`${PRODUCT_IMAGE_BASE_URL}${itemData[0].imageIndex}.png`}
              alt={cabinetTypeKey}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={9}>
          <TableContainer
            component={Paper}
            sx={{ boxShadow: 'none', borderRadius: 0 }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: '#F5F5F5',
                    borderRadius: '4px',
                    '&:first-of-type > *': {
                      borderBottom: 'none'
                    },
                    '& > *:first-of-type': {
                      borderTopLeftRadius: '4px',
                      borderBottomLeftRadius: '4px'
                    },
                    '& > *:last-of-type': {
                      borderTopRightRadius: '4px',
                      borderBottomRightRadius: '4px'
                    }
                  }}
                >
                  <TableCell>Model </TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Price</TableCell>
                  {isAuthenticated && (
                    <>
                      <TableCell
                        sx={{
                          minWidth: '100px'
                        }}
                      >
                        Stock
                      </TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Assembled</TableCell>
                      <TableCell
                        sx={{
                          minWidth: '80px'
                        }}
                      >
                        Hinges
                      </TableCell>
                      <TableCell></TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {itemData.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:last-of-type > *': {
                        borderBottom: 'none'
                      }
                    }}
                  >
                    <TableCell
                      onClick={() => navigate(`/products/${item.id}`)}
                      sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        cursor: 'pointer',
                        minWidth: '100px'
                      }}
                    >
                      {item.sku}
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-all',
                        hyphens: 'none'
                      }}
                    >
                      {item.description}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600
                      }}
                    >
                      {isNaN(item.price) ? (
                        <Typography
                          variant='inherit'
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate('/login')}
                        >
                          Sign in to view your pricing
                        </Typography>
                      ) : (
                        `$${Number(item.price).toFixed(2)}`
                      )}
                    </TableCell>

                    {isAuthenticated && (
                      <>
                        <TableCell
                          sx={{
                            color:
                              item.stock === 0 ? 'primary.main' : '#000000',
                            minWidth: '100px'
                          }}
                        >
                          {item.stock === 0 ? 'Out Of Stock' : 'In Stock'}
                        </TableCell>
                        <TableCell>
                          {item.stock !== 0 && (
                            <Select
                              value={quantities[item.sku] || 1}
                              onChange={e =>
                                handleQuantityChange(
                                  item.sku,
                                  Number(e.target.value)
                                )
                              }
                              sx={{
                                fontSize: '12px',
                                border: 'none',
                                width: '70px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '& .MuiOutlinedInput-input': {
                                  padding: '0px',
                                  textAlign: 'center'
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: 'none'
                                },
                                '& .MuiSelect-icon': {
                                  right: '8px'
                                }
                              }}
                            >
                              {Array.from(
                                {
                                  length: item.qtyRestrictions
                                    ? Math.min(item.qtyRestrictions, item.stock)
                                    : item.stock
                                },
                                (_, i) => i + 1
                              ).map(q => (
                                <MenuItem key={q} value={q}>
                                  {q}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        </TableCell>

                        <TableCell
                          sx={{
                            textAlign: 'center',
                            minHeight: '48px'
                          }}
                        >
                          {item.stock !== 0 && item.afmx !== 0 && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '48px'
                              }}
                            >
                              <Checkbox
                                size='small'
                                checked={isAssembled[item.sku] || false}
                                onChange={() => handleAssembledChange(item.sku)}
                              />
                            </Box>
                          )}
                        </TableCell>

                        <TableCell sx={{ minHeight: '48px' }}>
                          {item.stock !== 0 &&
                            isAssembled[item.sku] &&
                            item.isHingeable && (
                              <Select
                                value={hinges[item.sku] || ''}
                                onChange={e =>
                                  handleHingeChange(item.sku, e.target.value)
                                }
                                sx={{
                                  fontSize: '12px',
                                  border: 'none',
                                  width: '61px',
                                  '& .MuiOutlinedInput-input': {
                                    padding: '8px 0px'
                                  },
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none'
                                  }
                                }}
                              >
                                <MenuItem value='LEFT'>Left</MenuItem>
                                <MenuItem value='RIGHT'>Right</MenuItem>
                              </Select>
                            )}
                        </TableCell>

                        <TableCell>
                          {item.stock !== 0 && (
                            <IconButton
                              size='small'
                              onClick={() => handleAddToCart(item)}
                            >
                              <ShoppingBagIcon color='primary' />
                            </IconButton>
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <Divider
        sx={{
          width: '100%'
        }}
      />
    </Box>
  )
}

export default React.memo(ProductItem)
